import { ArrowLeft, CaretRight } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import { useBreakpoint } from 'src/hooks';
import styles from './OtherModelsButton.module.scss';
import { OtherModelsSelector } from 'src/v2/components/OtherModelsSelector';
import { SettingLLMModels } from 'src/types/settings';
import { Icon, IconName } from 'src/components/Icon';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { TextModelList } from 'src/v2/components/OtherModelsSelector/components/TextModelList';
import { useCompareMode } from 'src/hooks';
import { useActiveModel } from 'src/hooks/userSettingsHooks/useActiveModel';

interface OtherModelsButtonProps {
  onClose: () => void;
  handleOpenExternalModels: () => void;
  handleCloseExternalModels: () => void;
  isExternalModelsMenuOpened: boolean;
  models?: SettingLLMModels;
  showRegenerate?: boolean;
  onRegenerateClick?: (value: string) => void;
  shouldOpenExternalModelsByClick?: boolean;
  isComparingMode?: boolean;
  onComparingModelChange?: (value: string) => void;
  compareModel?: string;
}

const OTHER_LLM_ICONS_SET: IconName[] = [
  'chatGPTLogo',
  'claudeLogo',
  'googleGeminiProLogo',
  'deepSeekLogo',
];

export const OtherModelsButton = ({
  onClose,
  isExternalModelsMenuOpened,
  handleOpenExternalModels,
  handleCloseExternalModels,
  models,
  showRegenerate,
  onRegenerateClick,
  shouldOpenExternalModelsByClick = false,
  isComparingMode = false,
  onComparingModelChange,
  compareModel,
}: OtherModelsButtonProps) => {
  const { isMobileOrTablet } = useBreakpoint();

  const { changeCurrentModel } = useActiveModel();
  const { changeFirstCompareModel } = useCompareMode();

  const handleCloseSelector = () => {
    onClose();
    handleCloseExternalModels();
  };
  const handleChangeValue = (item: string) => {
    if (showRegenerate && !!onRegenerateClick) {
      onRegenerateClick(item);
    } else if (!isComparingMode) {
      changeCurrentModel(item);
      changeFirstCompareModel(item);
    } else if (isComparingMode) {
      onComparingModelChange?.(item);
    }

    handleCloseSelector();
  };

  const shouldOpenByClick =
    shouldOpenExternalModelsByClick && !isMobileOrTablet;
  const shouldOpenByHover =
    !shouldOpenExternalModelsByClick && !isMobileOrTablet;
  return (
    <>
      <div className={styles.headerRow}>
        <span className={styles.headerTitle}>Popular models</span>
      </div>
      <TextModelList
        compareModel={compareModel}
        isComparingMode={isComparingMode}
        onClick={handleChangeValue}
        modelsList={models?.slice(0, 5) as SettingLLMModels}
        showRegenerate={showRegenerate}
      />
      <TextDivider />

      <div
        className={styles.rowWrapper}
        onClick={shouldOpenByClick ? handleOpenExternalModels : undefined}
        onMouseEnter={shouldOpenByHover ? handleOpenExternalModels : undefined}
        onMouseLeave={shouldOpenByHover ? handleCloseExternalModels : undefined}
      >
        {((isMobileOrTablet && !isExternalModelsMenuOpened) ||
          !isMobileOrTablet) && (
          <div
            className={styles.labelWrapper}
            onClick={
              isMobileOrTablet && !isExternalModelsMenuOpened
                ? handleOpenExternalModels
                : undefined
            }
          >
            <span className={styles.title}>Additional 30+ AI models</span>
            <div className={styles.rightSideWrapper}>
              {!showRegenerate && (
                <div className={styles.llmIconsWrapper}>
                  {OTHER_LLM_ICONS_SET.map((item) => (
                    <Icon type={item} size={SVG_SIZE_M} key={item} />
                  ))}
                </div>
              )}
              <CaretRight size={SVG_SIZE_M} />
            </div>
          </div>
        )}

        {isMobileOrTablet && isExternalModelsMenuOpened && (
          <div
            className={styles.labelBackWrapper}
            onClick={handleCloseExternalModels}
          >
            <ArrowLeft size={SVG_SIZE_M} />
            <span className={styles.title}>Additional 30+ AI models</span>
          </div>
        )}
        {/* for external models by hover: */}
        {((isMobileOrTablet && isExternalModelsMenuOpened) ||
          !isMobileOrTablet) &&
          !shouldOpenExternalModelsByClick && (
            <OtherModelsSelector
              onCloseExternalModels={handleCloseExternalModels}
              onCloseSelector={handleCloseSelector}
              isOpen={isExternalModelsMenuOpened}
              modelsList={models?.slice(5)}
              showRegenerate={showRegenerate}
              onRegenerateClick={onRegenerateClick}
              isComparingMode={isComparingMode}
              onComparingModelChange={onComparingModelChange}
              compareModel={compareModel}
            />
          )}
      </div>
      {/* for external models by click: */}
      {((isMobileOrTablet && isExternalModelsMenuOpened) ||
        !isMobileOrTablet) &&
        shouldOpenExternalModelsByClick && (
          <OtherModelsSelector
            onCloseExternalModels={handleCloseExternalModels}
            onCloseSelector={handleCloseSelector}
            isOpen={isExternalModelsMenuOpened}
            modelsList={models}
            showRegenerate={showRegenerate}
            onRegenerateClick={onRegenerateClick}
            shouldOpenExternalModelsByClick
            isComparingMode={isComparingMode}
            onComparingModelChange={onComparingModelChange}
            compareModel={compareModel}
          />
        )}
    </>
  );
};
